import type { PrintTemplateSection } from "@repo/types";
import type { PrintFieldDef } from "@repo/print-pdf/arbre/print/template";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useMemo, useState } from "react";
import {
  frenchAnnouncements,
  preferItemsOverColumns,
  SCREEN_READER_INSTRUCTIONS,
} from "./dnd-helpers";
import { FieldCard, FieldCardBody } from "./field-card";
import type { DraftDispatch } from "./use-template-draft";

const COLUMN_PREFIX = "col-";
const isColumnId = (id: string) => id.startsWith(COLUMN_PREFIX);

function ColumnDropZone({
  col,
  highlighted,
  children,
}: {
  col: number;
  highlighted: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id: `${COLUMN_PREFIX}${col}` });
  return (
    <div
      ref={setNodeRef}
      aria-label={`Colonne ${col}`}
      className={`min-h-16 space-y-1 rounded-md border border-dashed p-1 transition-colors ${highlighted ? "border-primary bg-primary/10" : "border-border bg-muted/30"}`}
    >
      <div className="px-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Colonne {col}
      </div>
      {children}
    </div>
  );
}

interface Props {
  section: PrintTemplateSection;
  fieldByKey: Map<string, PrintFieldDef<any>>;
  dispatch: DraftDispatch;
}

/** Les colonnes de la section côte à côte : on glisse un champ d'une colonne à l'autre ou à l'intérieur d'une colonne. */
export function ColumnsEditor({ section, fieldByKey, dispatch }: Props) {
  const [activeField, setActiveField] = useState<string | null>(null);
  const [overColumn, setOverColumn] = useState<number | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const columnOf = (item: { col: number }) =>
    Math.min(Math.max(item.col, 1), section.columns);
  const columns = useMemo(
    () =>
      Array.from({ length: section.columns }, (_, index) =>
        section.items.filter(
          (item) => columnOf(item) === index + 1 && fieldByKey.has(item.field),
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [section.items, section.columns, fieldByKey],
  );
  const labelOf = (id: string) =>
    isColumnId(id)
      ? `colonne ${id.slice(COLUMN_PREFIX.length)}`
      : (fieldByKey.get(id)?.label ?? id);

  const columnUnder = (overId: string | undefined): number | null => {
    if (!overId) return null;
    if (isColumnId(overId)) return Number(overId.slice(COLUMN_PREFIX.length));
    const item = section.items.find((i) => i.field === overId);
    return item ? columnOf(item) : null;
  };

  const onDragStart = ({ active }: DragStartEvent) =>
    setActiveField(String(active.id));
  const onDragOver = ({ over }: DragOverEvent) =>
    setOverColumn(columnUnder(over ? String(over.id) : undefined));
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveField(null);
    setOverColumn(null);
    if (!over) return;
    const toCol = columnUnder(String(over.id));
    if (toCol === null) return;
    dispatch({
      type: "moveItemTo",
      sectionId: section.id,
      field: String(active.id),
      toCol,
      overField: isColumnId(String(over.id)) ? undefined : String(over.id),
    });
  };
  const onDragCancel = () => {
    setActiveField(null);
    setOverColumn(null);
  };

  const activeItem = activeField
    ? section.items.find((i) => i.field === activeField)
    : undefined;
  const activeDef = activeField ? fieldByKey.get(activeField) : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={preferItemsOverColumns(isColumnId)}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      accessibility={{
        announcements: frenchAnnouncements(labelOf),
        screenReaderInstructions: SCREEN_READER_INSTRUCTIONS,
      }}
    >
      <div
        className="grid gap-1.5"
        style={{
          gridTemplateColumns: `repeat(${section.columns}, minmax(0, 1fr))`,
        }}
      >
        {columns.map((items, index) => (
          <ColumnDropZone
            key={index}
            col={index + 1}
            highlighted={activeField !== null && overColumn === index + 1}
          >
            <SortableContext
              items={items.map((i) => i.field)}
              strategy={verticalListSortingStrategy}
            >
              {items.map((item) => (
                <FieldCard
                  key={item.field}
                  sectionId={section.id}
                  columns={section.columns}
                  item={item}
                  field={fieldByKey.get(item.field)!}
                  editing={editing === item.field}
                  onToggleEditing={() =>
                    setEditing((current) =>
                      current === item.field ? null : item.field,
                    )
                  }
                  dispatch={dispatch}
                />
              ))}
            </SortableContext>
          </ColumnDropZone>
        ))}
      </div>
      <DragOverlay dropAnimation={{ duration: 150 }}>
        {activeItem && activeDef ? (
          <FieldCardBody item={activeItem} field={activeDef} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
